import { toast } from "$global-providers/toast-provider";
import { Box, Button, styled, Typography } from "@material-ui/core";
import * as Sentry from "@sentry/react";
import { AccessionIdDisplay } from "../../../_lib/accession-id-display";
import { copyToClipboard } from "../../../_lib/copy-to-clipboard";
import { UltrasoundScanner } from "../../../_lib/epocus-icons";
import {
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";

const SectionCard = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

/**
 * PNE-491: explicit empty-state shown on an ePOCUS encounter that has an
 * accession but no images yet. POCUS is device-driven — there is no manual
 * upload path — so the affordance is to enter the accession on the ultrasound
 * machine, which links the DICOM scan back to this encounter once it replicates.
 */
export const AwaitingImagesSection: FC<{ accessionNumber: string }> = ({
  accessionNumber,
}) => {
  const handleCopy = async () => {
    try {
      await copyToClipboard(accessionNumber);
      toast.success("Accession ID copied to clipboard");
    } catch (clipboardError) {
      toast.error("Could not copy automatically — please copy the ID above");
      Sentry.captureException(clipboardError);
    }
  };

  return (
    <SectionCard>
      <Box display="flex" alignItems="center" style={{ gap: 10 }}>
        <UltrasoundScanner
          width={22}
          height={22}
          fill={EPOCUS_PRIMARY}
          aria-hidden
        />
        <Typography
          style={{
            fontWeight: 700,
            fontSize: rem(16),
            color: EPOCUS_TEXT_PRIMARY,
          }}
        >
          Awaiting images from device
        </Typography>
      </Box>
      <Typography
        style={{
          fontSize: rem(13),
          color: EPOCUS_TEXT_SECONDARY,
        }}
      >
        Enter accession{" "}
        <Box
          component="span"
          style={{ fontWeight: 700, color: EPOCUS_TEXT_PRIMARY }}
        >
          {accessionNumber}
        </Box>{" "}
        on the ultrasound machine to link the scan to this encounter. Images
        appear here automatically once the device sends them.
      </Typography>
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <AccessionIdDisplay aria-label={`Accession ID ${accessionNumber}`}>
          {accessionNumber}
        </AccessionIdDisplay>
        <Button
          variant="outlined"
          size="small"
          onClick={handleCopy}
          style={{
            borderRadius: 8,
            textTransform: "none",
            fontSize: rem(13),
            fontWeight: 600,
            borderColor: EPOCUS_PRIMARY,
            color: EPOCUS_PRIMARY,
            whiteSpace: "nowrap",
          }}
        >
          Copy ID
        </Button>
      </Box>
    </SectionCard>
  );
};
